import { AuthHttpService } from '.';
import type { QuestionAnswer, UserData } from '@/typings';
import { AppDispatch, getStoreState, store } from '@/store';
import {
  clearWillLogout,
  fetchCaqhInfo,
  fetchPlanList,
  logout,
  setWillLogout,
} from '../store';
import {
  fetchIdentity,
  fetchLoginQuestions,
  fetchPotentialTerminationDetail,
  fetchPotentialTerminationReasons,
  fetchSkippedTermination,
  login,
  skipTermination,
  updateIdentity,
  updatePotentialTerminationReason,
} from '../store';
import { ROUTES } from '@/routes';
import { adaptQuestionTypeToLoginQuestionType } from '@/adapters';
import { Singleton } from '@/decorators';
import {
  AuthKey,
  ContinuationUserData,
  Identity,
  PotentialTerminationReasonDetailExplanation,
  TokenClaims,
} from '../typings';
import { dispatchToast } from '@/ui-kit';
import { AccountControllerApi } from '@/api';
import type { StoreError } from '@/types/storeError.interface';
import type { AppError } from '@/types/errors.interface';
import { failure, success, type Result } from '@/types/result.interface';
import type { TurnstileProof } from '@/services/turnstile';
import { setTelemetryUser } from '@/services/application-insights';
import { CLIENT_ERROR_CODES } from '@/constants/clientErrorCodes';
import { createAppError } from '@/utils/errorCategoryMapper';

@Singleton
export class AuthService {
  private _dispatch: AppDispatch | null = null;

  constructor() {
    this._dispatch = store.dispatch;
  }

  destroy() {
    new AuthHttpService().destroy();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this as any).destroyInstance();
  }

  loginAlert() {
    dispatchToast({
      variant: 'success',
      title: 'Successful login',
      duration: 5000,
      id: 'login',
    });
  }

  logoutAlert() {
    dispatchToast({
      variant: 'success',
      title: 'Successful logout',
      duration: 5000,
      id: 'logout',
    });
  }

  /** Runs a thunk; resolves to its error when the request fails. */
  private async _load(
    thunk: (dispatch: AppDispatch) => Promise<StoreError | undefined>,
  ) {
    // The store's dispatch is typed to return unknown.
    return (await this._dispatch?.(thunk)) as StoreError | undefined;
  }

  async getLoginQuestions(key: AuthKey) {
    return this._load(fetchLoginQuestions(key));
  }

  async login(
    key: AuthKey,
    answers: QuestionAnswer[],
    userData: UserData,
    challenge?: TurnstileProof,
  ) {
    const mappedAnswers = answers.map(answer => ({
      ...answer,
      questionType: adaptQuestionTypeToLoginQuestionType(answer.questionType),
    }));

    return this._load(login(key, mappedAnswers, userData, challenge));
  }

  setWillLogout() {
    this._dispatch?.(setWillLogout());
  }

  clearWillLogout() {
    this._dispatch?.(clearWillLogout());
  }

  async logout() {
    this.clearWillLogout();
    await this._dispatch?.(logout());
    this.logoutAlert();
    // The full reload can beat TelemetryUserSync's effect, so clear the user here first.
    setTelemetryUser(null);
    window.location.assign(ROUTES.unauthenticated);
  }

  getToken() {
    return getStoreState().auth.token;
  }

  async getIdentity(key: AuthKey) {
    await this._dispatch?.(fetchIdentity(key));
  }

  async setIdentity(key: AuthKey, identity: Identity) {
    return this._load(updateIdentity(key, identity));
  }

  async getPotentialTerminationReasons(key: AuthKey) {
    return this._load(fetchPotentialTerminationReasons(key));
  }

  async getPotentialTerminationDetail(key: AuthKey) {
    return this._load(fetchPotentialTerminationDetail(key));
  }

  async setPotentialTerminationDetail(
    key: AuthKey,
    detail: PotentialTerminationReasonDetailExplanation,
  ) {
    return this._load(updatePotentialTerminationReason(key, detail));
  }

  async getTerminationSkipped(key: AuthKey) {
    return this._load(fetchSkippedTermination(key));
  }

  async setTerminationSkipped(key: AuthKey) {
    return this._load(skipTermination(key));
  }

  async getCaqhInfo(key: AuthKey) {
    return this._load(fetchCaqhInfo(key));
  }

  async continueWithCaqh(key: AuthKey, continuationUser: ContinuationUserData) {
    return await new AccountControllerApi().continueWithCAQH({
      key,
      continuationUser,
    });
  }

  async getPlanList(key: AuthKey) {
    return this._load(fetchPlanList(key));
  }

  getTokenClaims(): TokenClaims {
    const token = this.getToken();
    if (!token) {
      return {};
    }
    // Decode token to get claims
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return {};
    }
  }

  /** Resolves an invitation link's short code to its invitation key. */
  async decodeKey(
    code: string,
    challenge?: TurnstileProof,
  ): Promise<Result<AuthKey, AppError>> {
    const result = await new AccountControllerApi().decode({ code, challenge });
    if (!result.success) return result;
    // The API always sends a DTO, so a body-less 2xx is a proxy glitch: failed, not an invalid link.
    if (!result.data) {
      return failure(
        createAppError(
          CLIENT_ERROR_CODES.UNKNOWN_ERROR,
          'The invitation reply was empty.',
        ),
      );
    }
    return success(result.data.newKey);
  }
}
